import { useState, useEffect, useMemo, useCallback } from "react";
import { apiGet } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { Card } from "../components/dc";
import { formatNumber } from "../lib/format";

// Upload Status view (Data hub sub-tab). Green banner + a grid of supplier
// coverage cards with progress bars, matching the reference design. Wired to the
// same endpoints as before: /suppliers/upload-status + /price-files/summary.
export default function UploadStatus() {
  usePageTitle("Upload Status");
  const [supplierStatus, setSupplierStatus] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiGet("/suppliers/upload-status");
      setSupplierStatus(Array.isArray(d?.suppliers) ? d.suppliers : []);
    } catch { setSupplierStatus([]); }
    try {
      const files = await apiGet("/price-files/summary");
      const by = {};
      (Array.isArray(files) ? files : []).forEach((f) => {
        if (!by[f.supplier]) by[f.supplier] = { count: 0, months: new Set(), products: 0 };
        by[f.supplier].count++;
        by[f.supplier].months.add(f.month_year);
        by[f.supplier].products = Math.max(by[f.supplier].products, f.item_count || 0);
      });
      setSummary(by);
    } catch { setSummary({}); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loading = supplierStatus === null || summary === null;

  const cards = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary)
      .map(([supplier, info]) => {
        const st = supplierStatus?.find((s) => s.supplier === supplier);
        const covered = st?.file_months?.length ?? info.months.size;
        const missing = st?.missing_count ?? 0;
        const pct = covered + missing > 0 ? Math.round((covered / (covered + missing)) * 100) : 100;
        return { supplier, files: info.count, products: info.products, months: info.months.size, pct };
      })
      .sort((a, b) => a.supplier.localeCompare(b.supplier));
  }, [summary, supplierStatus]);

  const totalFiles = useMemo(() => cards.reduce((s, c) => s + c.files, 0), [cards]);
  const missingSuppliers = useMemo(
    () => (supplierStatus || []).filter((s) => !s.has_price_files),
    [supplierStatus]
  );

  if (loading) return <Card style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-3)" }}>Loading upload status…</Card>;
  if (cards.length === 0) return <Card style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-3)" }}>No price files uploaded yet.</Card>;

  return (
    <div data-testid="upload-status-page">
      <div style={{ background: "var(--color-bg-hemocyanin)", border: "1px solid rgba(52,211,153,0.25)", borderRadius: 14, padding: "18px 22px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ color: "#34d399", fontSize: 20 }}>📊</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15.5, color: "#34d399" }}>{formatNumber(totalFiles)} price files available</div>
          <div style={{ fontSize: 13, color: "var(--color-text-3)" }}>{cards.length} suppliers have uploaded price data</div>
        </div>
      </div>

      {missingSuppliers.length > 0 && (
        <div style={{ background: "var(--color-bg-hemocyanin)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 14, padding: "14px 18px", marginBottom: 20, fontSize: 14, color: "#fbbf24" }}>
          ⚠ {missingSuppliers.length} supplier{missingSuppliers.length !== 1 ? "s" : ""} still need price files — upload them from the Price Files tab.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 16 }}>
        {cards.map((c) => (
          <Card key={c.supplier} style={{ padding: "18px 20px", borderRadius: 14 }} data-testid={`pricefile-supplier-${c.supplier}`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: 700, fontSize: 15.5, color: "#67e0f0" }}>{c.supplier}</div>
              <div style={{ color: "#34d399", fontSize: 16 }}>✓</div>
            </div>
            <div style={{ fontSize: 12.5, color: "var(--color-text-3)", marginTop: 8 }}>{c.files} files · {formatNumber(c.products)} products</div>
            <div style={{ height: 6, borderRadius: 3, background: "rgba(255,255,255,0.08)", marginTop: 12, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${c.pct}%`, background: c.pct >= 90 ? "#34d399" : c.pct >= 70 ? "#fbbf24" : "#fb7185", borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 11.5, color: "#786a97", marginTop: 6 }}>{c.months} months covered</div>
          </Card>
        ))}
      </div>
    </div>
  );
}
